import React, { useCallback, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { TerminalCall } from "../../types/TerminalCall";
import { SelectField } from "../FormFields/RenderSelectField";
import { DateTimeField } from "../FormFields/RenderDateTimeField";
import GetVesselOptions from "../FormHelpers/VesselSelectOptions";
import { hideChildModal, hideModal, showChildModal } from "../../data/modal";
import { updateContainer } from "../../data/containers";
import { removeTerminalCall } from "../../data/terminalcall";
import { SelectMultipleField } from "../FormFields/RenderSelectMultipleField";
import GetLabelOptions from "../FormHelpers/LabelSelectOptions";
import { useTranslation } from "react-i18next";

import {
  Button,
  Divider,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

import { CheckboxField } from "../FormFields/RenderCheckboxField";
import GetVoyageOptions from "../FormHelpers/VoyageSelectOptions";
import { selectedFilteredVoyage } from "../../selectors/filterSelector";
import { AutocompleteField } from "../FormFields/RenderAutocompleteField";
import { getTerminals } from "../../data/terminals";
import { getVessels } from "../../data/vessels";
import { getVoyages } from "../../data/voyage";
import {
  createSelectedTerminalCall,
  updateSelectedTerminalCall,
} from "../../data/selectedterminalcalls";
import { useRefreshVoyageData } from "../hooks/useRefreshVoyageData";
import { getLabels } from "../../data/labels";

const ACTION_TYPE_LOADING = "loading";
const ACTION_TYPE_UNLOADING = "unloading";
const DOCKING_SIDE_STARBOARD = "starboard";
const DOCKING_SIDE_PORT = "port";

const TerminalCallForm = ({
  terminalCall,
  container,
  loadingUnloading = "",
  showStates = true,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { voyage } = useSelector(selectedFilteredVoyage);

  const openChildModal = useCallback(
    (opts: any) => dispatch(showChildModal(opts) as any),
    [dispatch]
  );

  const selectedData = useSelector((state: any) => ({
    vessels: state.vessels.data,
    terminals: state.terminals.data,
    voyages: state.voyages.data,
    labels: state.labels.data,
    modal: state.modal,
  }));

  useEffect(() => {
    dispatch(getTerminals() as any);
    dispatch(getVessels() as any);
    dispatch(getVoyages() as any);
    dispatch(getLabels() as any);
  }, [dispatch]);

  const close = () => {
    if (selectedData.modal.childModal) {
      dispatch(hideChildModal() as any);
      return;
    }

    dispatch(hideModal() as any);
  };

  const {
    handleSubmit,
    control,
    formState,
    setValue,
  } = useForm({
    defaultValues:
      terminalCall ??
      {
        ...new TerminalCall(),
        voyage: voyage?.["@id"] ?? null,
      },
  });

  const { isSubmitting } = formState;
  const refreshVoyageData = useRefreshVoyageData();

  const refreshPageDataAndClose = useCallback(async () => {
    await refreshVoyageData(voyage);
    close();
  }, [refreshVoyageData, voyage, selectedData.modal.childModal]);

  const onSubmit: SubmitHandler<TerminalCall> = async (data) => {
    const action = terminalCall
      ? updateSelectedTerminalCall(data)
      : createSelectedTerminalCall(data);

    await dispatch(action as any);
    await refreshPageDataAndClose();
  };

  const detachTerminalCall = async (container, loadingUnloading) => {
    let detachedTerminalCall = container;

    if (loadingUnloading === ACTION_TYPE_LOADING) {
      detachedTerminalCall = {
        ...container,
        loadingTerminal: null,
        loadingTerminalCall: null,
      };
    }

    if (loadingUnloading === ACTION_TYPE_UNLOADING) {
      detachedTerminalCall = {
        ...container,
        unloadingTerminal: null,
        unloadingTerminalCall: null,
      };
    }

    await dispatch(updateContainer(detachedTerminalCall) as any);
    await refreshPageDataAndClose();
  };

  const deleteTerminalCall = async () => {
    if (!terminalCall) {
      return;
    }

    await dispatch(removeTerminalCall(terminalCall) as any);
    await refreshPageDataAndClose();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container>
        <Grid item xs={12} sm={12}>
          <Grid item xs={12} sm={6}>
            <SelectField
              name="voyage"
              label={t("voyage")}
              control={control}
              options={GetVoyageOptions(selectedData.voyages)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" className="form-fields-title">
              Transport
            </Typography>

            <Grid item xs={12} className="form-fields-container">
              <Grid item xs={12} sm={6}>
                <SelectField
                  name="vessel"
                  label={t("vessel")}
                  control={control}
                  options={GetVesselOptions(selectedData.vessels, false)}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <AutocompleteField
                  name="terminal"
                  label={t("terminal")}
                  control={control}
                  options={selectedData.terminals}
                  required
                  onCreateClick={() =>
                    openChildModal({
                      title: t("addTerminal"),
                      description: t("createTerminalDescription"),
                      form: "terminal",
                      formProps: {
                        callback: setValue,
                        isChildModal: true,
                      },
                    })
                  }
                  onEditClick={(terminal) =>
                    openChildModal({
                      title: t("editTerminal"),
                      description: t("modifyTerminalDescription"),
                      form: "terminal",
                      formProps: {
                        terminal,
                        callback: setValue,
                        isChildModal: true,
                      },
                    })
                  }
                  onInfoClick={(terminal) =>
                    openChildModal({
                      title: terminal.name,
                      description: t("terminalMap"),
                      form: "terminalmapmodal",
                      formProps: {
                        terminal,
                      },
                    })
                  }
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} marginTop={1} marginBottom={1}>
            <Divider orientation="horizontal" />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" className="form-fields-title">
              ETA / ETD
            </Typography>

            <Grid item xs={12} className="form-fields-container">
              <Grid item xs={12} sm={4} className="form-field-datetime">
                <DateTimeField
                  name="planned"
                  label={t("planned")}
                  control={control}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4} className="form-field-datetime">
                <DateTimeField
                  name="arrival"
                  label={t("arrival")}
                  control={control}
                />
              </Grid>

              <Grid item xs={12} sm={4} className="form-field-datetime">
                <DateTimeField
                  name="departure"
                  label={t("departure")}
                  control={control}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} marginTop={1} marginBottom={1}>
            <Divider orientation="horizontal" />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" className="form-fields-title">
              {t("operationTime")}
            </Typography>

            <Grid item xs={12} className="form-fields-container">
              <Grid item xs={12} sm={4} className="form-field-datetime">
                <DateTimeField
                  name="operationStart"
                  label={t("started")}
                  control={control}
                />
              </Grid>

              <Grid item xs={12} sm={4} className="form-field-datetime">
                <DateTimeField
                  name="operationEnd"
                  label={t("finished")}
                  control={control}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} marginTop={1} marginBottom={1}>
            <Divider orientation="horizontal" />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" className="form-fields-title">
              {t("operation")}
            </Typography>

            <Grid item xs={12} className="form-fields-container">
              <Grid item xs={12} sm={4}>
                <CheckboxField
                  name="loadingPlanLocked"
                  label={t("lockLoadingPlan")}
                  control={control}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <SelectField
                  name="dockingSide"
                  label={t("dockingSide")}
                  control={control}
                  options={[
                    {
                      value: DOCKING_SIDE_PORT,
                      label: t("port"),
                    },
                    {
                      value: DOCKING_SIDE_STARBOARD,
                      label: t("starboard"),
                    },
                  ]}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} marginTop={1} marginBottom={1}>
            <Divider orientation="horizontal" />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" className="form-fields-title">
              {t("labels")}
            </Typography>

            <Grid item xs={12} sm={12} className="form-fields-container">
              <Grid item xs={12} sm={12}>
                <SelectMultipleField
                  name="labels"
                  label={t("labels")}
                  control={control}
                  options={GetLabelOptions(selectedData.labels)}
                  multiple={false}
                />
              </Grid>
            </Grid>
          </Grid>

          {terminalCall && showStates && (
            <>
              <Grid item xs={12} sm={12} marginTop={1} marginBottom={1}>
                <Divider orientation="horizontal" />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" className="form-fields-title">
                  States
                </Typography>

                <Grid item xs={12} sm={12} className="form-fields-container">
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="arrival-state-content"
                      id="arrival-state-header"
                    >
                      Arrival State
                    </AccordionSummary>

                    <AccordionDetails>
                      {terminalCall && terminalCall.arrivalState
                        ? JSON.stringify(terminalCall.arrivalState)
                        : ""}
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12} sm={12} className="form-fields-container">
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="departure-state-content"
                      id="departure-state-header"
                    >
                      Departure State
                    </AccordionSummary>

                    <AccordionDetails>
                      {terminalCall && terminalCall.departureState
                        ? JSON.stringify(terminalCall.departureState)
                        : ""}
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12} sm={12} className="form-fields-container">
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="barge-arrival-state-content"
                      id="barge-arrival-state-header"
                    >
                      Barge Arrival State
                    </AccordionSummary>

                    <AccordionDetails>
                      {terminalCall && terminalCall.bargearrivalState
                        ? JSON.stringify(terminalCall.bargearrivalState)
                        : ""}
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12} sm={12} className="form-fields-container">
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="barge-departure-state-content"
                      id="barge-departure-state-header"
                    >
                      Barge Departure State
                    </AccordionSummary>

                    <AccordionDetails>
                      {terminalCall && terminalCall.bargedepartureState
                        ? JSON.stringify(terminalCall.bargedepartureState)
                        : ""}
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>

        <Grid item xs={12} className="button-wrapper">
          <Grid container spacing={1}>
            <Grid item>
              <Button
                onClick={close}
                disabled={isSubmitting}
                variant="contained"
              >
                <CloseIcon fontSize="inherit" />
                &nbsp;{t("close")}
              </Button>
            </Grid>

            {container && (
              <Grid item>
                <Button
                  onClick={() =>
                    detachTerminalCall(container, loadingUnloading)
                  }
                  disabled={isSubmitting}
                  variant="contained"
                  color="error"
                >
                  <DeleteIcon fontSize="inherit" />
                  &nbsp;{t("unlinkContainer")}
                </Button>
              </Grid>
            )}

            {terminalCall && (
              <Grid item>
                <Button
                  onClick={deleteTerminalCall}
                  disabled={isSubmitting}
                  variant="contained"
                  color="error"
                >
                  <DeleteIcon fontSize="inherit" />
                  &nbsp;{t("delete")}
                </Button>
              </Grid>
            )}
          </Grid>

          <Button type="submit" disabled={isSubmitting} variant="contained">
            <SaveIcon fontSize="inherit" />
            &nbsp;{t("save")}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default TerminalCallForm;